<template>
    <div>
        <div :class="(isLiveDesignerView || !showUpdateButton) ? '' : 'px-10 py-6'">
            <div>
                <draggable
                    class="space-y-2 overflow-auto"
                    v-model="resources"
                    handle=".handle"
                    group="resources"
                    @start="drag = true"
                    direction="vertical"
                    chosenClass="bg-gray-100"
                    @end="drag = false"
                    item-key="$index"
                >
                    <template #item="{element}">
                        <Res :data="element" :options="resourceOptions" :unique_id="element.unique_id ?? uniqueId()"
                             @remove="removeResource" @toggle="toggleResource"/>
                    </template>
                </draggable>

                <div v-if="!resources.length" class="rounded border">
                    <div class="flex justify-center items-center text-center py-20 px-10">
                        <div class="max-w-2xl">
                            <div class="h-14 w-14 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 mb-4 mx-auto">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                    <path fill="none" d="M0 0h24v24H0z"/>
                                    <path d="M21 4H7a2 2 0 1 0 0 4h14v13a1 1 0 0 1-1 1H7a4 4 0 0 1-4-4V6a4 4 0 0 1 4-4h13a1 1 0 0 1 1 1v1zM5 18a2 2 0 0 0 2 2h12V10H7a3.982 3.982 0 0 1-2-.535V18zM20 7H7a1 1 0 1 1 0-2h13v2z"/>
                                </svg>
                            </div>
                            <h2 v-if="zeroFieldsTitle" class="font-semibold text-lg mb-2">{{ zeroFieldsTitle }}</h2>
                            <p class="text-gray-500 text-sm">{{ zeroFieldsMessage }}</p>
                            <div class="flex flex-col items-center gap-3 mt-4">
                                <options :auto-position="true" :is-right="false" width="!max-w-none !whitespace-normal w-[210px] sm:w-[380px] max-w-[calc(100vw-2rem)]">
                                    <template #toggle>
                                        <btn type="secondary" icon-name="more" icon-right="true" v-on:click.prevent>
                                            Add resource
                                        </btn>
                                    </template>
                                    <template v-slot:items="{ setShow }">
                                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-0.5 p-1.5">
                                            <button
                                                v-for="resource in partnerResourceOptions"
                                                :key="resource.type"
                                                type="button"
                                                class="flex items-start gap-2.5 px-3 py-2 text-left rounded hover:bg-gray-100 focus:outline-none dark:hover:bg-gray-800"
                                                @click="addResource(resource.type);setShow(false)"
                                            >
                                                <svg class="w-5 h-5 shrink-0 mt-0.5 text-gray-500 hidden" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-html="getResourceIcon(resource.type)"></svg>
                                                <span>
                                                    <span class="block text-sm font-medium text-gray-900 dark:text-gray-300">{{ resource.name }}</span>
                                                    <span class="block text-xs text-gray-400 leading-tight">{{ resource.description }}</span>
                                                </span>
                                            </button>
                                        </div>
                                    </template>
                                </options>
                                <span v-if="generateExampleUrl" class="text-gray-400 text-sm">or</span>
                                <btn
                                    v-if="generateExampleUrl"
                                    type="light"
                                    size="small"
                                    @click="generateExampleAssets()"
                                    :disabled="generatingExamples"
                                >
                                    <spinner
                                        v-if="generatingExamples"
                                        class="mr-1"
                                    />
                                    {{ generatingExamples ? 'Generating...' : 'Generate example assets' }}
                                </btn>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            <div v-if="resources.length" class="flex items-center gap-2 mt-2">
                <options :auto-position="true" :is-right="false" width="!max-w-none !whitespace-normal w-[210px] sm:w-[380px] max-w-[calc(100vw-2rem)]">
                    <template #toggle>
                        <btn type="secondary" icon-name="more" icon-right="true" v-on:click.prevent>
                            Add resource
                        </btn>
                    </template>
                    <template v-slot:items="{ setShow }">
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-0.5 p-1.5">
                            <button
                                v-for="resource in partnerResourceOptions"
                                :key="resource.type"
                                type="button"
                                class="flex items-start gap-2.5 px-3 py-2 text-left rounded hover:bg-gray-100 focus:outline-none dark:hover:bg-gray-800"
                                @click="addResource(resource.type);setShow(false)"
                            >
                                <svg class="w-5 h-5 shrink-0 mt-0.5 text-gray-500 hidden" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-html="getResourceIcon(resource.type)"></svg>
                                <span>
                                    <span class="block text-sm font-medium text-gray-900 dark:text-gray-300">{{ resource.name }}</span>
                                    <span class="block text-xs text-gray-400 leading-tight">{{ resource.description }}</span>
                                </span>
                            </button>
                        </div>
                    </template>
                </options>
            </div>
        </div>

        <input type="hidden" :name="name" :value="fieldsString">
        <div v-if="showUpdateButton" class="bg-gray-50 px-10 py-4 text-right rounded-b sticky bottom-0 z-40">
            <btn button-type="submit" type="primary" @click="saveResources()">
                <spinner
                    v-if="updatingResources"
                    :is-white="true"
                    class="mr-1.5"
                />
                {{ updatingResources ? 'Updating...' : 'Update' }}
            </btn>
        </div>
        <notification
            :show="showNotification"
            :title="notificationTitle"
            :type="notificationType"
            class="mb-20"
            @notificationClosed="showNotification = false"
            whitespace="whitespace-normal"
        >
            <template #message>
                {{ notificationMessage }}
            </template>
        </notification>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { v4 as uuid } from 'uuid';
import Res from './Res.vue';
import draggable from 'vuedraggable';
import Spinner from '../../../elements/Spinner.vue';
import {getResourceIcon} from "../resources-builder/helpers";

const partnerResourceOptions = [
    {type: 'assets', name: 'Assets', description: 'Downloadable files and media'},
    {type: 'banners', name: 'Banners', description: 'Promotional image banners'},
    {type: 'colors', name: 'Colors', description: 'Brand color palette'},
    {type: 'accordion', name: 'Accordion / FAQ', description: 'Collapsible Q&A sections'},
    {type: 'links', name: 'Links', description: 'Curated collection of links'},
    {type: 'videos', name: 'Video', description: 'Embedded video content'},
    {type: 'social_templates', name: 'Social media templates', description: 'Ready-made social posts'},
    {type: 'embed', name: 'Embed code', description: 'Custom HTML or embed code'},
];

const props = defineProps({
    name: {
        type: String,
        default: 'resources'
    },
    value: {
        type: [Object, Array],
        default: () => []
    },
    zeroFieldsMessage: {
        type: String,
        default: 'No resources available'
    },
    zeroFieldsTitle: {
        type: String,
        default: ''
    },
    updateUrl: {
        type: String,
        required: false
    },
    fileUploadUrl: {
        type: String,
        required: true
    },
    fileDeleteUrl: {
        type: String,
        required: true
    },
    generateExampleUrl: {
        type: String,
        required: false,
        default: ''
    },
    showUpdateButton: {
        type: Boolean,
        default: true
    },
    isLiveDesignerView: {
        type: Boolean,
        default: false
    },
    partnerTags: {
        type: Array,
        default: () => []
    },
});

const emit = defineEmits(['update:value']);

// Extract program ID from the update URL
const programId = computed(() => {
    const match = props.updateUrl?.match(/\/programs\/(\d+)\//);
    return match ? parseInt(match[1]) : null;
});

// Available partner tags for visibility filtering
const availablePartnerTags = ref([]);

const fetchAvailablePartnerTags = async () => {
    if (!programId.value) return;

    try {
        const response = await axios.get(
            route('app_int.program_partner_tags.list', { program: programId.value })
        );
        if (response.data?.partner_tags) {
            availablePartnerTags.value = response.data.partner_tags;
        }
    } catch (error) {
        console.error('Failed to fetch partner tags:', error);
    }
};

onMounted(() => {
    // If partner tags were provided via prop, use those; otherwise fetch them
    if (props.partnerTags.length > 0) {
        availablePartnerTags.value = props.partnerTags;
    } else {
        fetchAvailablePartnerTags();
    }

    // Listen for external save trigger (when embedded with showUpdateButton=false)
    document.addEventListener('resources-builder-save', saveResources);
});

onUnmounted(() => {
    document.removeEventListener('resources-builder-save', saveResources);
});

// Keep in sync if parent updates the prop
watch(() => props.partnerTags, (newTags) => {
    if (newTags && newTags.length > 0) {
        availablePartnerTags.value = newTags;
    }
});

// Reactive state
const resources = ref(props.value || []);
const showNotification = ref(false);
const notificationType = ref('');
const notificationTitle = ref('');
const notificationMessage = ref('');
const updatingResources = ref(false);
const generatingExamples = ref(false);
const drag = ref(false);

// Options object for child components
const resourceOptions = computed(() => ({
    update_url: props.updateUrl,
    file_delete_url: props.fileDeleteUrl,
    file_upload_url: props.fileUploadUrl,
    available_partner_tags: availablePartnerTags.value,
}));

// Computed
const fieldsString = computed(() => JSON.stringify(resources.value));

// Watchers - use getter function for reliable change detection
watch(() => resources.value, (newVal) => {
    emit('update:value', newVal);
}, { deep: true });

watch(() => props.value, (newVal) => {
    resources.value = newVal || [];
}, { deep: true });

// Helper to emit changes
const emitChanges = () => {
    emit('update:value', [...resources.value]);
};

// Methods
const saveResources = () => {
    updatingResources.value = true;
    axios
        .post(props.updateUrl, { resources: fieldsString.value })
        .then(response => {
            if (response.status === 200) {
                showNotification.value = true;
                notificationTitle.value = 'Done';
                notificationType.value = 'success';
                notificationMessage.value = 'Resource updated successfully';
            }
        })
        .catch(error => {
            showNotification.value = true;
            notificationTitle.value = 'Error';
            notificationType.value = 'warning';

            if (error?.response?.status === 422) {
                notificationMessage.value = error?.response?.data?.message ?? 'Resource update error, see console for error details.';
            } else {
                notificationMessage.value = 'Resource update error, see console for error details.';
            }
        })
        .finally(() => {
            updatingResources.value = false;
        });
};

const toggleResource = (resource) => {
    const isOpening = !resource.edit;
    if (isOpening) {
        resources.value.forEach(item => {
            if (item !== resource) {
                item.edit = false;
            }
        });
    }
    resource.edit = !resource.edit;
};

const addResource = (type) => {
    // Close all existing resources
    resources.value.forEach(item => {
        item.edit = false;
    });
    resources.value.push({ type, edit: true });
    emitChanges();
};

const removeResource = (resource) => {
    resources.value = resources.value.filter((item) => item !== resource);
    emitChanges();
};

const uniqueId = () => {
    return 'a' + uuid().split('-')[0];
};

const generateExampleAssets = () => {
    if (!props.generateExampleUrl) {
        return;
    }
    generatingExamples.value = true;
    axios
        .get(props.generateExampleUrl)
        .then(response => {
            if (response.status === 200 && response.data?.resources) {
                resources.value = response.data.resources;
                emitChanges();
                showNotification.value = true;
                notificationTitle.value = 'Done';
                notificationType.value = 'success';
                notificationMessage.value = 'Example assets generated successfully. Click Update to save.';
            }
        })
        .catch(error => {
            showNotification.value = true;
            notificationTitle.value = 'Error';
            notificationType.value = 'warning';
            notificationMessage.value = 'Failed to generate example assets.';
        })
        .finally(() => {
            generatingExamples.value = false;
        });
};
</script>
